import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

//Common
import { ThemeProvider } from "./components/common/VFX-UI/Theme/ThemeProvider";
import { vfxTheme } from "./components/common/VFX-UI/Theme/theme";

//App
import Layout from "./pages/layouts/_Layout";
import { Auth, AuthProvider } from "./Auth/authProvider";
import { getClaims } from "./utility/tokenHelper";
import {
    IsOrganizationDonor,
    IsOrganizationUser,
    IsCauwUser,
    userType,
    claims
} from "./utility/helpers/claimHelper";
import { updateApiAlert } from "./store/apiAlert";
import { setProfile } from "./store/userProfile";
import { useSnackbar, SnackbarKey } from "notistack";
import { useIdleTimer } from "react-idle-timer";
import { AppRoutes } from "./routes";
import { ApiAlertState } from "./app/model/api/types/apiTypes";

interface RootState {
    apiAlert: ApiAlertState;
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: { refetchOnWindowFocus: false, retry: 1 },
    },
});

const App = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const apiAlert = useSelector((state: RootState) => state.apiAlert);
    
    const pathName = window.location.pathname;
    const claim = getClaims();
    const auth = useContext(Auth);
    const dispatch = useDispatch();

    useEffect(() => {
        if (!auth.isAuthenticated()) return;

        dispatch(setProfile({ userIsAuthenticated: true, claims: claim }));
    }, [auth, pathName, navigate, dispatch, claim]);

    useEffect(() => {
        if (apiAlert) {
            const activeAlerts = apiAlert.alerts?.filter(
                (alert) => alert.show && alert.message !== null
            );
            if (activeAlerts?.length > 0) {
                activeAlerts.forEach((alert) => {
                    enqueueSnackbar(alert.message, {
                        anchorOrigin: { vertical: "top", horizontal: "center" },
                        variant: alert.severity,
                        autoHideDuration: 10000,
                        preventDuplicate: true,
                        onClose: (event: React.SyntheticEvent<any> | null, reason: string, key?: SnackbarKey) => {
                            dispatch(updateApiAlert(alert.id));
                            if (reason === "clickaway" && key !== undefined) {
                                closeSnackbar(key);
                            }
                        },
                    });
                });
            }
        }
    }, [apiAlert, closeSnackbar, dispatch, enqueueSnackbar]);

    const onIdle = () => {
        const origin = window.location.origin;
        window.location.href = origin + "/logout";
    };

    const { getRemainingTime, reset } = useIdleTimer({
        onIdle,
        timeout: 15 * 60 * 1000,
        throttle: 500,
    });

    useEffect(() => {
        const campaignId = claim?.[claims.CAMPAIGN_ID];
        const userTypeValue = claim?.[claims.USER_TYPE];
        const redirectDone = sessionStorage.getItem("pledgeRedirectDone");

        if (userTypeValue === userType.ORGANIZATION_DONOR && campaignId && !redirectDone) {
            sessionStorage.setItem("pledgeRedirectDone", "true");
            navigate(`/pledge/${campaignId}`);
        }

        if (pathName === "/") {
            if (IsOrganizationUser()) navigate("/reports-page");
            if (IsCauwUser()) navigate("/reports-page");
            if (IsOrganizationDonor() && !claim[claims.CAMPAIGN_ID]) navigate("/active-campaigns")
        }
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={vfxTheme}>
                <AuthProvider>
                    <Layout>
                        <AppRoutes />
                    </Layout>
                </AuthProvider>
            </ThemeProvider>
        </QueryClientProvider>
    )
};

export default App;
